/**
 * `cast_shadow`, GeometryInstance3D state (`renderer_scene_cull.cpp:732`) and never material
 * state, since a `.tres` material is shared. Its four values (`rendering_server.h:1494-1499`)
 * outgrow three's boolean `castShadow`, so the rest rides the per-surface draw hooks.
 */
import * as THREE from 'three';
import { ShadowCastingSetting } from '../godot/rendering/rendering';
import { UNRANGED_INSTANCE, poseColourDraw, skipColourDraw, surfaceDrawHooks, } from './surfaceDrawHooks';
/**
 * Godot's shadow pass keeps the material's own cull unless it uses double-sided
 * shadows (`render_forward_clustered.cpp:395-411`). three flips FrontSide↔BackSide
 * for the depth material against acne (`WebGLShadowMap.js:51`, applied at `:488`),
 * and every value undoes that. `shadowSide` is three's own per-material override.
 */
function materialCull(depthMaterial, material) {
    depthMaterial.side = material.shadowSide ?? material.side;
}
/**
 * DOUBLE_SIDED sets `cast_double_sided_shadows`, which drops the shadow pass's
 * cull. Safe on three's shared depth material: `getDepthMaterial` reassigns `side`
 * per object before the hook (`WebGLShadowMap.js:484-488`), so nothing leaks.
 */
function bothFaces(depthMaterial) {
    depthMaterial.side = THREE.DoubleSide;
}
const CAST_SETTINGS = {
    [ShadowCastingSetting.OFF]: {
        castShadow: false,
        rule: { colourDraw: poseColourDraw, shadowSide: materialCull },
    },
    [ShadowCastingSetting.ON]: {
        castShadow: true,
        rule: { colourDraw: poseColourDraw, shadowSide: materialCull },
    },
    [ShadowCastingSetting.DOUBLE_SIDED]: {
        castShadow: true,
        rule: { colourDraw: poseColourDraw, shadowSide: bothFaces },
    },
    [ShadowCastingSetting.SHADOWS_ONLY]: {
        castShadow: true,
        rule: { colourDraw: skipColourDraw, shadowSide: materialCull },
    },
};
/** Absent or unrecognised `cast_shadow` is Godot's default, ON. */
function castSettingOf(value) {
    return (CAST_SETTINGS[value] ?? CAST_SETTINGS[ShadowCastingSetting.ON]);
}
function castEffects({ castShadow, rule }, instance) {
    return Object.freeze({ castShadow, ...surfaceDrawHooks(rule, instance) });
}
// One frozen result per value: an unstable object would churn the mesh props.
const UNRANGED_EFFECTS = new Map(Object.values(CAST_SETTINGS).map((setting) => [setting, castEffects(setting, UNRANGED_INSTANCE)]));
/** The effects of an instance with no visibility range. */
export function shadowCastingEffects(value) {
    return UNRANGED_EFFECTS.get(castSettingOf(value));
}
/** The effects of an instance the scene cull and its drawer place. A new object per call. */
export function rangedShadowCastingEffects(value, instance) {
    return castEffects(castSettingOf(value), instance);
}
/**
 * What every object that draws part of a mesh sets: `cast_shadow` and the per-surface draw hooks.
 * three fires them per draw group, the shadow pair after `getDepthMaterial` has set the side
 * (`WebGLShadowMap.js:488,546,560`): the only per-surface reach into a draw of an object whose
 * materials and depth material three shares.
 */
export function drawHookProps(shadow) {
    return {
        castShadow: shadow.castShadow,
        receiveShadow: true,
        onBeforeRender: shadow.onBeforeRender,
        onAfterRender: shadow.onAfterRender,
        onBeforeShadow: shadow.onBeforeShadow,
        onAfterShadow: shadow.onAfterShadow,
    };
}
/** The JSX props, applied to an object built outside JSX, such as a GLB's meshes. */
export function applyShadowCasting(object, effects) {
    object.castShadow = effects.castShadow;
    object.onBeforeRender = effects.onBeforeRender;
    object.onAfterRender = effects.onAfterRender;
    object.onBeforeShadow = effects.onBeforeShadow;
    object.onAfterShadow = effects.onAfterShadow;
}
//# sourceMappingURL=shadowCasting.js.map